/**
 * Viewport Image Loader Web Component
 * Viewport-observed lazy-loading component for images
 * Automatically loads and displays images when the element enters the viewport
 * 
 * Usage:
 * <viewport-image-loader root-margin="50px" threshold="0.1">
 *   <img data-src="image.jpg" data-srcset="..." alt="..." />
 * </viewport-image-loader>
*/
class ViewportImageLoader extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.isIntersecting = false;
    this.rootMargin = this.getAttribute('root-margin') || '50px';
    this.threshold = parseFloat(this.getAttribute('threshold')) || 0.1;
  }

  connectedCallback() {
    if (!window.IntersectionObserver) {
      this.showImages();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.isIntersecting) {
            this.isIntersecting = true;
            this.showImages();
            this.observer.unobserve(this);
          }
        });
      },
      {
        rootMargin: this.rootMargin,
        threshold: this.threshold,
      }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  showImages() {
    this.classList.add('ec:visible');

    const images = this.querySelectorAll('img');
    
    // 懒加载图片：将 data-src 复制到 src，将 data-srcset 复制到 srcset
    images.forEach((img) => {
      if (img.dataset.src && !img.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
      if (img.dataset.srcset && !img.srcset) {
        img.srcset = img.dataset.srcset;
        img.removeAttribute('data-srcset');
      }
    });

    // 处理 picture 元素中的 source 标签
    const sources = this.querySelectorAll('source[data-src], source[data-srcset]');
    sources.forEach((source) => {
      // 处理 data-srcset（优先）
      if (source.dataset.srcset && !source.srcset) {
        source.srcset = source.dataset.srcset;
        source.removeAttribute('data-srcset');
      }
      // 处理 data-src（作为后备）
      if (source.dataset.src && !source.srcset) {
        source.srcset = source.dataset.src;
        source.removeAttribute('data-src');
      }
    });

    this.dispatchEvent(
      new CustomEvent('images-visible', {
        bubbles: true,
        detail: { images: Array.from(images) },
      })
    );
  }
}

if (!customElements.get('viewport-image-loader')) {
  customElements.define('viewport-image-loader', ViewportImageLoader);
}
